/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from "react";
import { Box } from "@mui/material";
import useApi from "../../../hooks/useApi";
import { RoleDto, UserDto } from "../../../types/Role";
import { getAllRolesByUser } from "../../../services/roleService";
import { MultiSelectDropdown } from "../../../components/ui/MultiSelectDropdown";

interface RoleWithCheck extends RoleDto {
  isChecked: boolean;
}

interface UserWithCheck extends UserDto {
  isChecked: boolean;
}

interface RoleUserSelectorProps {
  onUsersChange: (selectedUsers: UserDto[]) => void;
  onRolesChange?: (selectedRoles: RoleDto[]) => void;
  toLabel: string;
  roleError?: string;
  userError?: string;
}

export const RoleUserSelector: React.FC<RoleUserSelectorProps> = ({
  onUsersChange,
  onRolesChange,
  toLabel,
  roleError,
  userError,
}) => {
  const [roles, setRoles] = useState<RoleWithCheck[]>([]);
  const [users, setUsers] = useState<UserWithCheck[]>([]);
  const { executeApi } = useApi();

  useEffect(() => {
    const fetchRoles = async () => {
      const data = await executeApi<number, RoleDto[]>(getAllRolesByUser, 1);
      if (!data) return;
      const rolesWithCheck = data.map((r) => ({ ...r, isChecked: false }));
      setRoles(rolesWithCheck);

      // Populate all users from all roles by default (unchecked)
      const userMap = new Map<number, UserWithCheck>();
      data.forEach((role) => {
        role.users.forEach((user) => {
          if (!userMap.has(user.userId)) {
            userMap.set(user.userId, { ...user, isChecked: false });
          }
        });
      });
      setUsers(Array.from(userMap.values()));
    };
    fetchRoles();
  }, []);

  useEffect(() => {
    const selectedRoles = roles.filter((r) => r.isChecked);
    const hasSelectedRoles = selectedRoles.length > 0;

    if (hasSelectedRoles) {
      // Show only users from selected roles, auto-checked
      const userMap = new Map<number, UserWithCheck>();
      selectedRoles.forEach((role) => {
        role.users.forEach((user) => {
          if (!userMap.has(user.userId)) {
            userMap.set(user.userId, { ...user, isChecked: true });
          }
        });
      });
      const combinedUsers = Array.from(userMap.values());
      setUsers(combinedUsers);
      onUsersChange(combinedUsers.filter((u) => u.isChecked));
    } else {
      // No role selected — show all users unchecked
      const userMap = new Map<number, UserWithCheck>();
      roles.forEach((role) => {
        role.users.forEach((user) => {
          if (!userMap.has(user.userId)) {
            userMap.set(user.userId, { ...user, isChecked: false });
          }
        });
      });
      const allUsers = Array.from(userMap.values());
      setUsers(allUsers);
      onUsersChange([]);
    }
  }, [roles]);

  const handleRolesChange = (updatedRoles: RoleWithCheck[]) => {
    setRoles(updatedRoles);
    onRolesChange?.(updatedRoles.filter((r) => r.isChecked));
  };

  const handleUsersChange = (updatedUsers: UserWithCheck[]) => {
    setUsers(updatedUsers);
    const selected = updatedUsers.filter((u) => u.isChecked);
    onUsersChange(selected);
  };

  return (
    <Box>
      <Box sx={{ mt: 1 }}>
        <MultiSelectDropdown
          label={`To ${toLabel} Roles`}
          items={roles}
          keySelector={(r) => r.roleId}
          labelSelector={(r) => r.roleName}
          onChange={handleRolesChange}
          error={!!roleError}
          helperText={roleError}
        />
      </Box>
      <Box sx={{ mt: 2 }}>
        <MultiSelectDropdown
          label={`To ${toLabel} Users`}
          items={users}
          keySelector={(u) => u.userId}
          labelSelector={(u) => u.userName}
          onChange={handleUsersChange}
          error={!!userError}
          helperText={userError}
        />
      </Box>
    </Box>
  );
};
